import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTheme } from '../contexts/ThemeContext'
import { coursesAPI } from '../config/api'
import 'primeicons/primeicons.css'

interface ExpiredSubscriptionModalProps {
  isOpen: boolean
  onClose: () => void
  onDismiss?: () => void
}

export default function ExpiredSubscriptionModal({ isOpen, onClose, onDismiss }: ExpiredSubscriptionModalProps) {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [renewalCourseId, setRenewalCourseId] = useState<number | null>(null)

  useEffect(() => {
    if (!isOpen) return

    // Fetch khóa học gia hạn (lấy khóa học online đầu tiên có QR code)
    const fetchRenewalCourse = async () => {
      try {
        const coursesData = await coursesAPI.getPublicCourses({ type: 'online' })
        const coursesList = Array.isArray(coursesData)
          ? coursesData
          : Array.isArray(coursesData?.results)
            ? coursesData.results
            : Array.isArray(coursesData?.data)
              ? coursesData.data
              : []
        
        // Tìm khóa học có QR code (ưu tiên khóa học đầu tiên)
        const courseWithQR = coursesList.find((c: any) => c.payment_qr_code) || coursesList[0]
        if (courseWithQR) {
          setRenewalCourseId(courseWithQR.id)
        }
      } catch (error) {
        // Error fetching renewal course
      }
    }

    fetchRenewalCourse()
  }, [isOpen])

  const handleDismiss = () => {
    // Chỉ đóng modal, không lưu trạng thái
    // Modal sẽ hiển thị lại khi chuyển trang hoặc truy cập lại khóa học
    onClose()
    if (onDismiss) {
      onDismiss()
    }
  }

  const handleSkip = () => {
    // Lưu trạng thái bỏ qua cho trang hiện tại
    const dismissedKey = `expired_subscription_modal_dismissed_${location.pathname}`
    localStorage.setItem(dismissedKey, 'true')
    onClose()
    if (onDismiss) {
      onDismiss()
    }
  }

  const handleContinue = () => {
    // Lưu flag đã bấm "Tiếp tục thanh toán" để không hiển thị modal ở trang thanh toán
    localStorage.setItem('renewal_continue_clicked', 'true')
    
    // Chuyển hướng đến trang thanh toán với courseId
    if (renewalCourseId) {
      navigate(`/course/online/payment?courseId=${renewalCourseId}`)
    } else {
      // Nếu chưa có courseId, chuyển đến trang khóa học online
      navigate('/course/online')
    }
    onClose()
    if (onDismiss) {
      onDismiss()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleDismiss}
      />
      
      {/* Modal */}
      <div className={`relative z-10 w-full max-w-md rounded-2xl border-2 ${
        theme === 'dark' 
          ? 'bg-black/95 border-red-500/50' 
          : 'bg-white border-red-400/50'
      } shadow-2xl`}>
        {/* Close Button */}
        <button
          onClick={handleDismiss}
          className={`absolute top-4 right-4 z-20 w-10 h-10 rounded-full flex items-center justify-center transition ${
            theme === 'dark'
              ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
              : 'bg-red-400/20 hover:bg-red-400/30 text-red-600'
          }`}
        >
          <i className="pi pi-times text-lg"></i>
        </button>

        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <i className={`pi pi-exclamation-triangle text-3xl ${theme === 'dark' ? 'text-red-400' : 'text-red-500'} animate-pulse`}></i>
              <h2 className={`text-xl md:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Kỳ Hạn Đã Hết
              </h2>
            </div>
            <p className={`text-base md:text-lg font-semibold ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
              ⚠️ Kỳ hạn của bạn đã hết!
            </p>
            <p className={`text-sm mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Vui lòng gia hạn để tiếp tục truy cập khóa học
            </p>
          </div>

          {/* Info Box */}
          <div className={`border-2 rounded-xl p-4 mb-6 ${theme === 'dark' ? 'bg-red-500/10 border-red-500/30 text-gray-300' : 'bg-red-50 border-red-400 text-gray-700'}`}>
            <p className="text-sm md:text-base text-center">
              Bạn cần thanh toán để gia hạn kỳ hạn và tiếp tục truy cập các khóa học đã đăng ký.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleContinue}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 text-white text-sm md:text-base font-bold hover:opacity-90 transition flex items-center justify-center gap-2"
            >
              <i className="pi pi-arrow-right"></i>
              <span>Tiếp tục thanh toán</span>
            </button>
            <button
              onClick={handleSkip}
              className={`w-full py-3 rounded-xl text-sm md:text-base font-semibold transition ${theme === 'dark' ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-900 hover:bg-gray-300'}`}
            >
              Bỏ qua (không hỏi lại)
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

